import { defineStore } from 'pinia';

import type { IDocezyFailedSync, IDocezyStatus } from '@/interfaces/agency/integrations/docezy';
import docezyService from '@/services/agency/integrations/docezy';

interface DocezyState {
  status: IDocezyStatus | null;
  isLoading: boolean;
  isProvisioning: boolean;
  isSyncing: boolean;
  syncInitiated: boolean;
  failures: IDocezyFailedSync[];
  failuresTotal: number;
  isLoadingFailures: boolean;
  error: string | null;
}

export const useDocezyStore = defineStore('docezy', {
  state: (): DocezyState => ({
    status: null,
    isLoading: false,
    isProvisioning: false,
    isSyncing: false,
    syncInitiated: false,
    failures: [],
    failuresTotal: 0,
    isLoadingFailures: false,
    error: null
  }),

  getters: {
    isConnected: state => state.status?.connected === true,
    connectedAt: state => state.status?.connectedAt ?? null,
    docezyTenantSlug: state => state.status?.docezyTenantSlug ?? null,
    lastSyncedAt: state => state.status?.lastSyncedAt ?? null
  },

  actions: {
    async fetchStatus() {
      this.isLoading = true;
      this.error = null;
      try {
        const { data } = await docezyService.getStatus();

        this.status = data;
      } catch {
        this.error = 'Failed to load Docezy integration status.';
      } finally {
        this.isLoading = false;
      }
    },

    async provision() {
      this.isProvisioning = true;
      this.error = null;
      try {
        await docezyService.provision();
        await this.pollUntilConnected();
      } catch (err: any) {
        this.error = err?.response?.data?.message ?? 'Failed to start Docezy provisioning.';
      } finally {
        this.isProvisioning = false;
      }
    },

    async sync() {
      this.isSyncing = true;
      this.syncInitiated = false;
      this.error = null;
      try {
        await docezyService.sync();
        this.syncInitiated = true;
        setTimeout(async () => {
          await this.fetchFailures();
          await this.fetchStatus();
          this.syncInitiated = false;
          this.isSyncing = false;
        }, 30_000);
      } catch (err: any) {
        this.error = err?.response?.data?.message ?? 'Sync failed. Please try again.';
        this.isSyncing = false;
      }
    },

    async fetchFailures(page = 1, limit = 50) {
      this.isLoadingFailures = true;
      try {
        const { data } = await docezyService.getFailures(page, limit);

        this.failures = data.items;
        this.failuresTotal = data.total;
      } catch {
        // silent — failures list is non-critical
      } finally {
        this.isLoadingFailures = false;
      }
    },

    async pollUntilConnected(maxAttempts = 10, intervalMs = 2000) {
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, intervalMs));
        await this.fetchStatus();
        if (this.isConnected) return;
      }
    }
  }
});
